// ─────────────────────────────────────────────────────────────────────────────
// SAP Fiori primitives — a self-contained design layer for modules that are
// meant to read as SAP (the creator HQ today). Deliberately NOT wired into
// design-system/index.js: the AONE editorial system (tokens.js) stays the app
// default, and a page opts into Fiori by importing from here.
//
// The component props mirror their Aone* counterparts one-for-one, so a page
// converts by aliasing on import:
//   import { FioriButton as AoneButton } from '../design-system/fiori';
// …and every existing call site keeps working.
//
// SAP Fiori STRUCTURE (dense grids, object-page header, icon tab bar, object
// status as coloured text, flat surfaces, 4px control radius) in AONE's OWN
// colours — deep burgundy accent, Love White paper, editorial semantics. No
// SAP blue: the house palette is White / Burgundy / Ink Black, and tokens.js
// deliberately keeps info purple rather than blue. Values mirror tokens.js so
// the Fiori pages sit next to the editorial ones without a colour clash.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useId } from 'react';
import { fieldLabel } from './fieldLabel';
import { T as AONE_T } from './tokens';
import { MobileRecordCard, pickCardFields } from './mobileRecordCard';
import { useResponsive } from '../hooks/useResponsive';

export const FIORI = {
  shell: '#3F0F16',                    // deep burgundy shell strip
  // `blue*` keys keep their Fiori names (they are the "brand interactive"
  // slot) but carry AONE burgundy — one rename would touch every call site.
  blue: AONE_T.accent, blueHover: AONE_T.accentHover, blueActive: '#3F0F16',
  blueSoft: AONE_T.accentSoft, blueMid: AONE_T.accentLight,
  ink: AONE_T.text, sub: AONE_T.textSec, muted: AONE_T.textMuted,
  ok: AONE_T.success, warn: AONE_T.warning, bad: AONE_T.danger, neutral: AONE_T.neutral,
  okBg: AONE_T.successBg, warnBg: AONE_T.warningBg, badBg: AONE_T.dangerBg, neutralBg: AONE_T.surfaceAlt,
  okBorder: AONE_T.successBorder, warnBorder: AONE_T.warningBorder, badBorder: AONE_T.dangerBorder,
  bg: AONE_T.bg, surface: AONE_T.surface,
  line: AONE_T.divider, border: AONE_T.border, borderStrong: AONE_T.borderStrong,
  gridHead: AONE_T.surfaceAlt, headLine: AONE_T.borderStrong,
  rowHover: '#FBF7F1', rowSel: AONE_T.accentSoft,
  font: AONE_T.font, mono: AONE_T.fontMono, display: AONE_T.fontDisplay,
  r: 4, rCard: 8,
  shadow: '0 1px 2px rgba(29,26,22,0.06)',
  shadowLg: '0 10px 30px rgba(29,26,22,0.14)',
};

const F = FIORI;
const merge = (...o) => Object.assign({}, ...o.filter(Boolean));

// ── Role palette — the editorial pages colour things by "role" (mint, coral,
// lemon…). Fiori has no pastel roles, only semantic states, so every role
// collapses onto ok / warn / bad / brand. Exported so a converting page can
// shadow its own ROLE table with one import instead of hand-mapping each one.
export const FIORI_ROLE = {
  mint:    { fill: F.okBg,     ink: F.ok },
  sage:    { fill: F.okBg,     ink: F.ok },
  lemon:   { fill: F.warnBg,   ink: F.warn },
  apricot: { fill: F.warnBg,   ink: F.warn },
  blush:   { fill: F.badBg,    ink: F.bad },
  rose:    { fill: F.badBg,    ink: F.bad },
  coral:   { fill: F.badBg,    ink: F.bad },
  lilac:   { fill: F.blueSoft, ink: F.blue },
};

// ── Grid cells — SAP responsive-table look: hairline rows, grey uppercase head
export const fioriTh = {
  background: F.gridHead, textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: F.sub, padding: '9px 12px', borderBottom: `1px solid ${F.headLine}`,
  whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: F.font,
};
export const fioriTd = {
  padding: '10px 12px', borderBottom: `1px solid ${F.line}`, fontSize: 13,
  verticalAlign: 'top', color: F.ink, fontFamily: F.font,
};

// ── Object status — Fiori shows state as coloured TEXT, not a filled pill.
const STATE_BY_STATUS = {
  approved: 'ok', active: 'ok', delivered: 'ok', paid: 'ok', compliant: 'ok', live: 'ok', done: 'ok',
  pending: 'warn', pending_review: 'warn', requested: 'warn', paused: 'warn', expiring: 'warn', unpaid: 'warn',
  rejected: 'bad', danger: 'bad', overdue: 'bad', churned: 'bad', cancelled: 'bad', expired: 'bad', failed: 'bad',
};
export function fioriState(status) {
  return STATE_BY_STATUS[String(status || '').toLowerCase()] || 'none';
}
const STATE_COLOR = { ok: F.ok, warn: F.warn, bad: F.bad, information: F.blue, none: F.neutral };
// Editorial "role" names → Fiori semantic state (see FIORI_ROLE below).
const STATE_BY_ROLE = {
  mint: 'ok', sage: 'ok', lemon: 'warn', apricot: 'warn',
  blush: 'bad', rose: 'bad', coral: 'bad', lilac: 'information',
};
const STATE_ICON = { ok: '✓', warn: '!', bad: '✕', information: 'ⓘ', none: '' };

export function FioriStatus({ status, label, size = 'md', style, state }) {
  const st = state || fioriState(status);
  const color = STATE_COLOR[st] || F.neutral;
  const text = label || (String(status).charAt(0).toUpperCase() + String(status).slice(1));
  return (
    <span style={merge({
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontFamily: F.font, fontSize: size === 'sm' ? 11 : 13, fontWeight: 600,
      color, whiteSpace: 'nowrap', lineHeight: 1.4,
    }, style)}>
      {STATE_ICON[st] && <span aria-hidden style={{ fontSize: '0.92em', fontWeight: 700 }}>{STATE_ICON[st]}</span>}
      {text}
    </span>
  );
}

// Bordered variant for counts/tags that need a boundary (codes, badges).
export function FioriTag({ state = 'none', children, title, onClick, style }) {
  const color = STATE_COLOR[state] || F.neutral;
  const bg = state === 'ok' ? F.okBg : state === 'warn' ? F.warnBg : state === 'bad' ? F.badBg : state === 'information' ? F.blueSoft : F.neutralBg;
  return (
    <span title={title} onClick={onClick} style={merge({
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px',
      border: `1px solid ${color}`, borderRadius: 10, background: bg, color,
      fontFamily: F.font, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
      cursor: onClick ? 'pointer' : undefined,
    }, style)}>{children}</span>
  );
}

// ── Button — emphasized / transparent / ghost / negative / positive
const BTN_VARIANT = {
  primary:   { background: F.blue, color: '#fff', border: `1px solid ${F.blue}` },
  emphasized:{ background: F.blue, color: '#fff', border: `1px solid ${F.blue}` },
  secondary: { background: '#fff', color: F.blue, border: `1px solid ${F.blue}` },
  ghost:     { background: 'transparent', color: F.blue, border: '1px solid transparent' },
  transparent:{ background: 'transparent', color: F.blue, border: '1px solid transparent' },
  danger:    { background: '#fff', color: F.bad, border: `1px solid ${F.bad}` },
  negative:  { background: F.bad, color: '#fff', border: `1px solid ${F.bad}` },
  positive:  { background: '#fff', color: F.ok, border: `1px solid ${F.ok}` },
  warning:   { background: '#fff', color: F.warn, border: `1px solid ${F.warn}` },
  accent:    { background: F.shell, color: '#fff', border: `1px solid ${F.shell}` },
};
const BTN_SIZE = { sm: { padding: '3px 10px', fontSize: 12 }, md: { padding: '5px 12px', fontSize: 13 }, lg: { padding: '8px 16px', fontSize: 14 } };

export function FioriButton({ children, variant = 'primary', size = 'md', onClick, disabled = false, type = 'button', style, icon, title }) {
  const v = BTN_VARIANT[variant] || BTN_VARIANT.secondary;
  const [hover, setHover] = useState(false);
  const hoverBg = variant === 'primary' || variant === 'emphasized' ? F.blueHover
    : variant === 'negative' ? '#920000'
    : variant === 'accent' ? '#2b3c4d'
    : variant === 'danger' ? F.badBg
    : variant === 'positive' ? F.okBg
    : variant === 'warning' ? F.warnBg
    : F.blueSoft;
  return (
    <button type={type} onClick={onClick} disabled={disabled} title={title}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={merge(v, BTN_SIZE[size] || BTN_SIZE.md, {
        fontFamily: F.font, fontWeight: 600, borderRadius: F.r,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
        transition: 'background 80ms linear',
        ...(hover && !disabled ? { background: hoverBg } : null),
      }, style)}>
      {icon && <span style={{ fontSize: '1em' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ── IconTabBar — text tabs with a 3px selection bar, counts as Fiori badges
// `onSelect` is the name two direct call sites use. Unhandled, their tab bars
// were inert — the click landed on nothing at all.
export function FioriTabBar({ tabs, active, onChange, onSelect, style }) {
  onChange = onChange || onSelect;
  return (
    <div style={merge({
      display: 'flex', gap: 0, background: F.surface, borderBottom: `1px solid ${F.border}`,
      overflowX: 'auto', fontFamily: F.font, boxShadow: 'inset 0 -1px 0 0 ' + F.border,
    }, style)}>
      {tabs.map(tab => {
        const on = tab.key === active;
        return (
          <button key={tab.key} onClick={() => onChange(tab.key)}
            style={{
              position: 'relative', border: 'none', background: 'transparent',
              padding: '11px 16px 10px', cursor: 'pointer', flexShrink: 0,
              fontFamily: F.font, fontSize: 14, fontWeight: on ? 700 : 400,
              color: on ? F.blue : F.ink, display: 'flex', alignItems: 'center', gap: 7,
              borderBottom: `3px solid ${on ? F.blue : 'transparent'}`,
            }}
            onMouseEnter={e => { if (!on) e.currentTarget.style.background = F.rowHover; }}
            onMouseLeave={e => { if (!on) e.currentTarget.style.background = 'transparent'; }}
          >
            {tab.icon && <span style={{ fontSize: 14 }}>{tab.icon}</span>}
            {tab.label}
            {tab.count !== undefined && tab.count !== null && (
              <span style={{
                fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, padding: '0 5px',
                borderRadius: 9, background: on ? F.blue : F.gridHead, color: on ? '#fff' : F.sub,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Dynamic page header + page background (object-page style)
// `iconTile`, `meta` and `status` are AonePageShell's props — accepted so an
// editorial page converts by aliasing the import. Fiori has no coloured icon
// tile, so it renders as the flat burgundy shell square SAP uses for an object
// avatar, and `meta` becomes the dot-separated sub-line under the title.
// Nearly every list page hands the shell a row of buttons whose first one
// creates the thing the page lists. On a phone that row stacks above the list
// as desk furniture, and 100-odd pages were never going to be edited one at a
// time to say so. So the shell finds that button itself and puts it where a
// thumb reaches.
//
// Deliberately narrow: ONLY a button that says it creates something. A page
// whose first action is Export or Save keeps its action row, because a round
// "+" that exports a spreadsheet is worse than a button in the wrong place.
const CREATES = /^\s*[+＋]?\s*(new|add|create|raise|record|start)\b/i;

function textIn(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textIn).join(' ');
  return textIn(node?.props?.children);
}

function deriveFab(actions) {
  let found = null;
  const walk = (node) => {
    if (found || node == null || typeof node !== 'object') return;
    if (Array.isArray(node)) { node.forEach(walk); return; }
    const label = textIn(node.props?.children).trim();
    if (node.props?.onClick && !node.props?.disabled && CREATES.test(label)) {
      found = { label, onClick: node.props.onClick };
      return;
    }
    walk(node.props?.children);
  };
  walk(actions);
  return found;
}

export function FioriPage({ title, subtitle, actions, kpis, breadcrumb, iconTile, meta, status, children, style, mobileFab }) {
  const metaLine = (Array.isArray(meta) ? meta : [meta]).filter(Boolean);
  const { isMobile } = useResponsive();
  const fab = mobileFab || (isMobile ? deriveFab(actions) : null);
  return (
    // 100% of the scroll container, NOT 100vh. Every page renders inside
    // #aone-content, which is the viewport minus the 44px shell bar — so a
    // 100vh page was always 44px taller than the box holding it, and every
    // screen in the app carried 44px of phantom scroll even when empty.
    <div style={merge({ background: F.bg, minHeight: '100%', padding: '0 0 72px', fontFamily: F.font, color: F.ink }, style)}>
      {(title || actions || kpis) && (
        <div style={{
          background: F.surface, borderBottom: `1px solid ${F.border}`,
          padding: '12px 24px 0', boxShadow: '0 1px 1px rgba(29,45,62,0.06)',
        }}>
          {breadcrumb?.length > 0 && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              {breadcrumb.map((c, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ color: F.muted, fontSize: 12 }}>/</span>}
                  <span style={{ fontSize: 12, color: c.path ? F.blue : F.sub, cursor: c.path ? 'pointer' : 'default' }} onClick={c.onClick}>{c.label}</span>
                </React.Fragment>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              {iconTile?.letter && (
                <div aria-hidden style={{
                  width: 34, height: 34, flexShrink: 0, borderRadius: F.r, background: F.shell, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700,
                }}>{iconTile.letter}</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: F.ink, letterSpacing: '-0.01em' }}>{title}</h1>
                  {status?.label && <FioriStatus status={status.label} label={status.label} state={STATE_BY_ROLE[status.role] || 'information'} size="sm" />}
                </div>
                {subtitle && <div style={{ fontSize: 13, color: F.sub, marginTop: 2 }}>{subtitle}</div>}
                {metaLine.length > 0 && (
                  <div style={{ fontSize: 12, color: F.muted, marginTop: 3 }}>{metaLine.join(' · ')}</div>
                )}
              </div>
            </div>
            {actions && !(isMobile && fab) && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>{actions}</div>}
          </div>
          {/* On a phone a kpi row is a HEADLINE, not a strip.
              Four tiles at 375px wrap into three rows and push the first record
              below the fold — which is what Purchase Orders did. A phone
              carries the one number you came for and, when something is wrong,
              one number that says so. `headline` forces the big slot; a danger
              tone picks the right-hand one. The desk still gets all of them. */}
          {kpis?.length > 0 && isMobile && (() => {
            const big = kpis.find((k) => k.headline) || kpis[0];
            const alarm = kpis.find((k) => k !== big && k.alarm && Number(k.rawValue ?? k.value) !== 0);
            return (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 10 }}>
                <div onClick={big.onClick} style={{ cursor: big.onClick ? 'pointer' : 'default', minWidth: 0, display: 'flex', alignItems: 'flex-end', gap: 7 }}>
                  <span style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: F.ink, fontVariantNumeric: 'tabular-nums' }}>{big.value}</span>
                  <span style={{ fontSize: 13, color: F.muted, fontWeight: 600, paddingBottom: 4 }}>{big.unitLabel || big.label}</span>
                </div>
                {alarm && (
                  <div onClick={alarm.onClick} style={{ marginLeft: 'auto', textAlign: 'right', cursor: alarm.onClick ? 'pointer' : 'default', flexShrink: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: F.red || '#991B1B', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{alarm.value}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: F.muted }}>{alarm.label}</div>
                  </div>
                )}
              </div>
            );
          })()}
          {kpis?.length > 0 && !isMobile && (
            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 12 }}>
              {kpis.map((k, i) => (
                <div key={k.label} onClick={k.onClick} style={{
                  padding: i === 0 ? '0 20px 0 0' : '0 20px', borderLeft: i > 0 ? `1px solid ${F.line}` : 'none',
                  minWidth: 92, cursor: k.onClick ? 'pointer' : undefined,
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: k.tone || (FIORI_ROLE[k.role]?.ink) || F.ink, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
                  <div style={{ fontSize: 11, color: F.sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{k.label}</div>
                  {k.trend && <div style={{ fontSize: 11, color: F.muted, marginTop: 1 }}>{k.trend}</div>}
                </div>
              ))}
            </div>
          )}
          <div style={{ height: 12 }} />
        </div>
      )}
      <div style={{ padding: '16px 24px 0' }}>{children}</div>
      {/* The one action a phone needs, where a thumb reaches. Passing mobileFab
          also suppresses the desktop action row — three text buttons stacked
          above the list is desk furniture. */}
      {isMobile && fab && (
        <div onClick={fab.onClick} role="button" aria-label={fab.label || 'Add'}
          style={{
            position: 'fixed', right: 16, bottom: 78, width: 52, height: 52, borderRadius: '50%',
            background: F.accent || '#6E1F2B', color: '#fff', display: 'grid', placeItems: 'center',
            fontSize: 26, fontWeight: 300, lineHeight: 1, cursor: 'pointer', zIndex: 40,
            boxShadow: '0 8px 20px rgba(110,31,43,0.36)',
          }}>{fab.icon || '+'}</div>
      )}
    </div>
  );
}

// ── KPI / numeric tile (Fiori "Numeric Content" card)
export function FioriTile({ label, value, state = 'none', onClick, selected, style }) {
  const color = STATE_COLOR[state] || F.ink;
  return (
    <div onClick={onClick} style={merge({
      background: F.surface, border: `1px solid ${selected ? F.blue : F.border}`,
      borderRadius: F.rCard, padding: '10px 14px', cursor: onClick ? 'pointer' : 'default',
      boxShadow: selected ? `0 0 0 1px ${F.blue}` : 'none', fontFamily: F.font, textAlign: 'left',
    }, style)}>
      <div style={{ fontSize: 20, fontWeight: 700, color: state === 'none' ? F.ink : color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11, color: F.sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Message strip
export function FioriMessageStrip({ type = 'information', children, style }) {
  const map = {
    information: { c: F.blue, bg: F.blueSoft, b: F.blueMid, i: 'ⓘ' },
    success: { c: F.ok, bg: F.okBg, b: F.okBorder, i: '✓' },
    warning: { c: F.warn, bg: F.warnBg, b: F.warnBorder, i: '!' },
    error: { c: F.bad, bg: F.badBg, b: F.badBorder, i: '✕' },
  }[type] || {};
  return (
    <div style={merge({
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
      background: map.bg, border: `1px solid ${map.b}`, borderRadius: F.r,
      fontFamily: F.font, fontSize: 13, color: F.ink,
    }, style)}>
      <span style={{ color: map.c, fontWeight: 700, flexShrink: 0 }}>{map.i}</span>
      {children}
    </div>
  );
}

// ── Section header (Fiori form/group title)
export function FioriSectionHeader({ title, subtitle, actions, style }) {
  return (
    <div style={merge({ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, borderBottom: `1px solid ${F.line}`, paddingBottom: 6, fontFamily: F.font }, style)}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: F.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: F.sub, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {actions}
    </div>
  );
}

// ── Illustrated message (empty state)
export function FioriEmptyState({ icon = 'ⓘ', title, message, action, style }) {
  return (
    <div style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      padding: '44px 24px', textAlign: 'center', fontFamily: F.font,
    }, style)}>
      <div style={{ fontSize: 34, opacity: 0.55, marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: F.ink }}>{title}</div>
      {message && <div style={{ fontSize: 13, color: F.sub, marginTop: 6, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>{message}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

// ── Inputs — square, grey border, blue focus underline
const fieldBase = {
  width: '100%', boxSizing: 'border-box', padding: '5px 10px', minHeight: 30,
  border: `1px solid ${F.borderStrong}`, borderBottom: `2px solid ${F.borderStrong}`,
  borderRadius: F.r, background: F.surface, color: F.ink,
  fontFamily: F.font, fontSize: 13, outline: 'none',
};
export const fioriFieldBase = fieldBase;

function Labelled({ label, required, hint, error, children, style }) {
  const { text: labelText, showStar } = fieldLabel(label, required);
  // The label was rendered NEXT to the control, not attached to it: no htmlFor,
  // no id. Visually identical, and for a screen reader the field is unnamed —
  // across every Fiori form in the app. Tapping the label also did nothing.
  // useId keeps it stable across renders and unique across fields.
  const id = useId();
  const control = React.isValidElement(children) && !children.props.id
    ? React.cloneElement(children, { id })
    : children;
  return (
    <div style={merge({ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: F.font }, style)}>
      {label && (
        <label htmlFor={id} style={{ fontSize: 12, fontWeight: 400, color: F.sub }}>
          {labelText}{showStar && <span style={{ color: F.bad, marginLeft: 2 }}>*</span>}
        </label>
      )}
      {control}
      {error ? <span style={{ fontSize: 11, color: F.bad }}>{error}</span>
        : hint ? <span style={{ fontSize: 11, color: F.muted }}>{hint}</span> : null}
    </div>
  );
}

export function FioriInput({ label, value, onChange, placeholder, type = 'text', required, hint, error, disabled, style, inputStyle, onKeyDown, min, max, step, inputMode, autoComplete, maxLength, readOnly, name, list }) {
  const [focus, setFocus] = useState(false);
  return (
    <Labelled label={label} required={required} hint={hint} error={error} style={style}>
      <input type={type} value={value ?? ''} placeholder={placeholder} disabled={disabled}
        min={min} max={max} step={step} inputMode={inputMode} autoComplete={autoComplete}
        maxLength={maxLength} readOnly={readOnly} name={name} list={list}
        onChange={e => onChange?.(e.target.value)} onKeyDown={onKeyDown}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={merge(fieldBase, focus ? { borderColor: F.blue, borderBottomColor: F.blue } : null,
          error ? { borderColor: F.bad, borderBottomColor: F.bad } : null,
          disabled ? { background: F.neutralBg, color: F.muted } : null, inputStyle)} />
    </Labelled>
  );
}

export function FioriSelect({ label, value, onChange, options = [], placeholder, required, hint, disabled, style, selectStyle }) {
  const [focus, setFocus] = useState(false);
  return (
    <Labelled label={label} required={required} hint={hint} style={style}>
      <select value={value ?? ''} disabled={disabled}
        onChange={e => onChange?.(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={merge(fieldBase, { cursor: disabled ? 'not-allowed' : 'pointer', appearance: 'none', paddingRight: 26,
          backgroundImage: `linear-gradient(45deg, transparent 50%, ${F.blue} 50%), linear-gradient(135deg, ${F.blue} 50%, transparent 50%)`,
          backgroundPosition: 'calc(100% - 14px) 13px, calc(100% - 9px) 13px',
          backgroundSize: '5px 5px, 5px 5px', backgroundRepeat: 'no-repeat' },
        focus ? { borderColor: F.blue, borderBottomColor: F.blue } : null, selectStyle)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Labelled>
  );
}

// ── Search field with the Fiori magnifier affordance
export function FioriSearch({ value, onChange, placeholder, hint, style, width }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={merge({ position: 'relative', width: width || '100%', fontFamily: F.font }, style)}>
      <input value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={merge(fieldBase, { paddingRight: 30 }, focus ? { borderColor: F.blue, borderBottomColor: F.blue } : null)} />
      <span aria-hidden style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', color: F.blue, fontSize: 14, pointerEvents: 'none' }}>⌕</span>
      {hint && <span style={{ position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: F.sub }}>{hint}</span>}
    </div>
  );
}

// ── Card / panel — prop-compatible with AoneCard (headerRight/padding/onClick)
export function FioriCard({ title, subtitle, actions, headerRight, children, style, bodyStyle, padding, onClick, accentTop }) {
  const right = actions || headerRight;
  return (
    <div onClick={onClick} style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      boxShadow: F.shadow, fontFamily: F.font, overflow: 'hidden',
      borderTop: accentTop ? `3px solid ${F.blue}` : undefined,
      cursor: onClick ? 'pointer' : undefined,
    }, style)}>
      {(title || right) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: `1px solid ${F.line}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: F.ink }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: F.sub, marginTop: 1 }}>{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      <div style={merge({ padding: padding ?? '12px 14px' }, bodyStyle)}>{children}</div>
    </div>
  );
}

// ── KPI card — Fiori numeric-content tile. Drop-in for AoneKpiCard.
// `sub` is a plain context line under the value. It is deliberately not
// `deltaLabel`: that only renders inside the delta block, behind a ▲/▼ arrow,
// so a tile with no delta had nowhere to put "22 with UE data" at all.
export function FioriKpiCard({ label, value, delta, deltaLabel, sub, icon, color, variant, onClick, style }) {
  const tone = color || F.ink;
  const up = typeof delta === 'number' ? delta >= 0 : String(delta || '').startsWith('+');
  return (
    <div onClick={onClick} style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      padding: '12px 14px', fontFamily: F.font, cursor: onClick ? 'pointer' : 'default',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
    }, style)}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: F.sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: tone, fontVariantNumeric: 'tabular-nums', marginTop: 3, lineHeight: 1.15 }}>{value}</div>
        {(delta !== undefined && delta !== null && delta !== '') && (
          <div style={{ fontSize: 12, marginTop: 3, color: up ? F.ok : F.bad, fontWeight: 600 }}>
            {up ? '▲' : '▼'} {typeof delta === 'number' ? `${Math.abs(delta)}%` : delta}
            {deltaLabel && <span style={{ color: F.muted, fontWeight: 400 }}> {deltaLabel}</span>}
          </div>
        )}
        {sub && <div style={{ fontSize: 11, color: F.muted, marginTop: 3, lineHeight: 1.35 }}>{sub}</div>}
      </div>
      {icon && <span style={{ fontSize: 18, opacity: 0.6, flexShrink: 0 }}>{icon}</span>}
    </div>
  );
}

// ── Responsive table — Fiori grid look, AoneDataTable's exact API so pages
// convert by aliasing the import. Columns: {key,label|header,accessor,render,
// align,numeric,width,sortable,mobileHide}.
// A page's `style` on a data table is written for the TABLE — most often a
// `minWidth` so its columns do not crush, sometimes an explicit width. Applied
// to the card column it makes a 390px phone render a 900px list and scroll
// sideways, which is what Trademarks did. 138 pages pass one of these.
function deskTableStyle(style) {
  if (!style) return style;
  const { minWidth, width, maxWidth, ...rest } = style;
  void minWidth; void width; void maxWidth;
  return rest;
}

export function FioriDataTable({
  columns = [], rows = [], rowKey = 'id', getRowKey, onRowClick,
  selectable = false, selectedKeys = [], onSelectChange,
  sortKey, sortDir = 'asc', onSort,
  loading = false, empty, stickyHeader = true, style, maxHeight,
  rowStyle, rowActions, footer,
  // ── Expansion & grouping ────────────────────────────────────────────────
  // Added because eleven pages hand-roll a <table> purely to get these two
  // things — Production alone keeps 18 of them. Both are strictly opt-in:
  // with renderExpanded and groupBy absent, every branch below is skipped and
  // the rendered output is unchanged for the 112 pages already using this.
  //   renderExpanded(row)            → node shown in a full-width row beneath
  //   expandedKeys / onExpandChange  → controlled; omit for internal state
  //   groupBy(row)                   → group key, or null for no group
  //   renderGroupHeader(key, rows)   → node for the group's header row
  renderExpanded, expandedKeys, onExpandChange,
  groupBy, renderGroupHeader,
}) {
  // Defensive: a section that hands us `{items:[…]}` (or null while loading)
  // must degrade to an empty grid, never crash the tab.
  const list = Array.isArray(rows) ? rows : [];
  const { isMobile } = useResponsive();
  const [internalSort, setInternalSort] = useState({ key: sortKey, dir: sortDir });
  const [ownExpanded, setOwnExpanded] = useState([]);
  const expanded = expandedKeys || ownExpanded;
  const setExpanded = onExpandChange || setOwnExpanded;
  const isExpanded = (row, i) => expanded.includes(keyOf(row, i));
  const toggleExpand = (row, i) => {
    const k = keyOf(row, i);
    setExpanded(expanded.includes(k) ? expanded.filter(x => x !== k) : [...expanded, k]);
  };
  const colLabel = (c) => c.label ?? c.header;
  const colValue = (c, row) => (c.accessor ? c.accessor(row) : row[c.key]);
  const colAlign = (c) => c.align || (c.numeric ? 'right' : 'left');
  const keyOf = (row, i) => (getRowKey ? getRowKey(row, i) : row[rowKey] ?? i);

  const handleSort = (key) => {
    if (!key) return;
    const dir = internalSort.key === key && internalSort.dir === 'asc' ? 'desc' : 'asc';
    setInternalSort({ key, dir });
    onSort?.(key, dir);
  };
  const sorted = onSort ? list : [...list].sort((a, b) => {
    if (!internalSort.key) return 0;
    const col = columns.find(c => c.key === internalSort.key);
    const av = (col ? colValue(col, a) : a[internalSort.key]) ?? '';
    const bv = (col ? colValue(col, b) : b[internalSort.key]) ?? '';
    const cmp = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
    return internalSort.dir === 'asc' ? cmp : -cmp;
  });

  const isSelected = (row) => selectedKeys.includes(row[rowKey]);
  const toggleRow = (row) => {
    const k = row[rowKey];
    onSelectChange?.(isSelected(row) ? selectedKeys.filter(x => x !== k) : [...selectedKeys, k]);
  };
  const toggleAll = () => onSelectChange?.(selectedKeys.length === list.length ? [] : list.map(r => r[rowKey]));

  const wrap = merge({
    background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
    overflowX: 'auto', overflowY: maxHeight ? 'auto' : 'visible', maxHeight,
    boxShadow: F.shadow, WebkitOverflowScrolling: 'touch',
  }, style);

  // ── Mobile: card per row ───────────────────────────────────────────────────
  // A grid of 8 columns inside a 390px viewport is a horizontal scrollbar, and
  // a horizontal scrollbar is how a phone page becomes unreadable: the first
  // column is an ID nobody needs and the status you came to check sits off the
  // right edge. AoneDataTable has rendered cards here for a while; this table
  // did not, so which component a page happened to import decided whether it
  // worked on a phone. Same shape, same `mobileHide` opt-out, so the two agree.
  if (isMobile && !loading) {
    // Cards, not a transposed table. See design-system/mobileRecordCard.js —
    // the card is shared with components.js AoneDataTable so the two render
    // paths cannot drift.
    const fields = pickCardFields(columns, { colLabel, colValue });
    return (
      <div style={merge({ display: 'flex', flexDirection: 'column', gap: 8 }, deskTableStyle(style))}>
        {sorted.length === 0 && (empty || (
          <div style={{
            background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
            padding: '28px 18px', textAlign: 'center', color: F.muted,
            fontFamily: F.font, fontSize: 14,
          }}>
            No records found.
          </div>
        ))}
        {sorted.map((row, ri) => {
          const f = fields(row);
          return (
            <MobileRecordCard
              key={keyOf(row, ri)}
              {...f}
              actions={f.actions || (rowActions ? rowActions(row) : null)}
              selected={isSelected(row)}
              accent={F.blue}
              onOpen={onRowClick ? () => onRowClick(row) : null}
              extraStyle={rowStyle?.(row, ri)}
              lead={selectable ? (
                <div style={{ marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={isSelected(row)}
                    onChange={() => toggleRow(row)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 18, height: 18, accentColor: F.blue }}
                  />
                </div>
              ) : null}
            />
          );
        })}
        {footer && <div style={{ padding: '10px 4px', fontSize: 12, color: F.sub, fontFamily: F.font }}>{footer}</div>}
      </div>
    );
  }

  const th = { ...fioriTh, position: stickyHeader ? 'sticky' : undefined, top: stickyHeader ? 0 : undefined, zIndex: 2 };
  const colSpan = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (renderExpanded ? 1 : 0);

  return (
    <div style={wrap}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: F.font }}>
        <thead>
          <tr>
            {renderExpanded && <th style={{ ...th, width: 34 }} aria-label="Expand" />}
            {selectable && (
              <th style={{ ...th, width: 38 }}>
                <input type="checkbox" checked={list.length > 0 && selectedKeys.length === list.length}
                  onChange={toggleAll} style={{ cursor: 'pointer', accentColor: F.blue }} />
              </th>
            )}
            {columns.map((col, ci) => (
              <th key={ci} onClick={col.sortable ? () => handleSort(col.key) : undefined}
                style={{ ...th, width: col.width, textAlign: colAlign(col), cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {colLabel(col)}
                  {col.sortable && (
                    <span style={{ fontSize: 10, color: internalSort.key === col.key ? F.blue : F.muted }}>
                      {internalSort.key === col.key ? (internalSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  )}
                </span>
              </th>
            ))}
            {rowActions && <th style={{ ...th, width: 56 }} aria-label="Row actions" />}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={colSpan} style={{ ...fioriTd, padding: '40px 14px', textAlign: 'center', color: F.sub }}>
              {empty || 'No records found.'}
            </td></tr>
          )}
          {sorted.map((row, ri) => {
            const extra = rowStyle ? rowStyle(row, ri) : null;
            const resting = (extra && extra.background) || (isSelected(row) ? F.rowSel : F.surface);
            // A group header is emitted on the first row of each run, so the
            // rows stay a flat list and sorting/selection are unaffected.
            const gKey = groupBy ? groupBy(row) : null;
            const newGroup = groupBy && gKey != null && (ri === 0 || groupBy(sorted[ri - 1]) !== gKey);
            const open = renderExpanded ? isExpanded(row, ri) : false;
            const body = (
              <tr onClick={() => onRowClick?.(row)}
                style={{ background: resting, cursor: onRowClick ? 'pointer' : 'default', ...(extra || null) }}
                onMouseEnter={e => { e.currentTarget.style.background = F.rowHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = resting; }}>
                {renderExpanded && (
                  <td style={{ ...fioriTd, width: 34, textAlign: 'center', cursor: 'pointer', userSelect: 'none', color: F.sub }}
                    onClick={e => { e.stopPropagation(); toggleExpand(row, ri); }}>
                    <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
                  </td>
                )}
                {selectable && (
                  <td style={{ ...fioriTd, width: 38 }} onClick={e => { e.stopPropagation(); toggleRow(row); }}>
                    <input type="checkbox" checked={isSelected(row)} onChange={() => toggleRow(row)}
                      style={{ cursor: 'pointer', accentColor: F.blue }} />
                  </td>
                )}
                {columns.map((col, ci) => {
                  const v = colValue(col, row);
                  return (
                    <td key={ci} style={{
                      ...fioriTd, textAlign: colAlign(col), verticalAlign: 'middle', maxWidth: col.width || undefined,
                      fontVariantNumeric: col.numeric ? 'tabular-nums' : undefined,
                    }}>
                      {col.render ? col.render(v, row) : (v ?? <span style={{ color: F.muted }}>—</span>)}
                    </td>
                  );
                })}
                {rowActions && (
                  <td onClick={e => e.stopPropagation()} style={{ ...fioriTd, width: 56, textAlign: 'right', verticalAlign: 'middle' }}>
                    {rowActions(row)}
                  </td>
                )}
              </tr>
            );
            // Without either feature this is the same single <tr> as before,
            // just handed back through a Fragment.
            if (!newGroup && !renderExpanded) return React.cloneElement(body, { key: keyOf(row, ri) });
            const panel = open ? renderExpanded(row) : null;
            return (
              <React.Fragment key={keyOf(row, ri)}>
                {newGroup && (
                  <tr>
                    <td colSpan={colSpan} style={{ ...fioriTd, background: F.neutralBg, fontWeight: 700, color: F.ink }}>
                      {renderGroupHeader
                        ? renderGroupHeader(gKey, sorted.filter(r => groupBy(r) === gKey))
                        : String(gKey)}
                    </td>
                  </tr>
                )}
                {body}
                {panel != null && (
                  <tr>
                    <td colSpan={colSpan} style={{ ...fioriTd, background: F.neutralBg, padding: 0 }}>{panel}</td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {footer && (
        <div style={{ padding: '8px 14px', borderTop: `1px solid ${F.border}`, background: F.neutralBg, fontSize: 12, color: F.sub, fontFamily: F.font }}>
          {footer}
        </div>
      )}
    </div>
  );
}

// ── Pager — Fiori table footer. Hidden when everything fits on one page.
export function FioriPager({ page, setPage, total, pageSize = 50, label = 'row', style }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) {
    return total > 0 ? (
      <div style={merge({ padding: '7px 12px', fontSize: 12, color: F.sub, fontFamily: F.font, borderTop: `1px solid ${F.line}`, background: F.neutralBg }, style)}>
        {total.toLocaleString('en-IN')} {label}{total === 1 ? '' : 's'}
      </div>
    ) : null;
  }
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const go = (p) => {
    setPage(Math.min(pages, Math.max(1, p)));
    document.getElementById('aone-content')?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <div style={merge({
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      padding: '7px 12px', borderTop: `1px solid ${F.line}`, background: F.neutralBg, fontFamily: F.font,
    }, style)}>
      <span style={{ fontSize: 12, color: F.sub }}>
        {from.toLocaleString('en-IN')}–{to.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')} {label}s
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FioriButton variant="transparent" size="sm" disabled={page <= 1} onClick={() => go(1)}>« First</FioriButton>
        <FioriButton variant="transparent" size="sm" disabled={page <= 1} onClick={() => go(page - 1)}>‹ Prev</FioriButton>
        <span style={{ fontSize: 12, color: F.ink, fontWeight: 600, minWidth: 92, textAlign: 'center' }}>Page {page} of {pages}</span>
        <FioriButton variant="transparent" size="sm" disabled={page >= pages} onClick={() => go(page + 1)}>Next ›</FioriButton>
        <FioriButton variant="transparent" size="sm" disabled={page >= pages} onClick={() => go(pages)}>Last »</FioriButton>
      </div>
    </div>
  );
}

// ── Alert banner — message strip with a title. Drop-in for AoneAlertBanner.
export function FioriAlertBanner({ variant, tone, title, message, action, dismissible = true, onDismiss, style }) {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  // `tone` is accepted too — some pages pass it instead of `variant`.
  variant = variant || tone || 'info';
  // 'warn' as well as 'warning': several callers pass the short form and every
  // one of them has been rendering as a blue information strip — the Creator
  // Marketplace "not usable yet" notice among them, which is the opposite of
  // the emphasis it was written for.
  const type = variant === 'danger' || variant === 'error' ? 'error'
    : variant === 'warning' || variant === 'warn' ? 'warning'
    : variant === 'success' ? 'success' : 'information';
  return (
    <FioriMessageStrip type={type} style={style}>
      <span style={{ flex: 1 }}>
        {title && <strong>{title}</strong>}{title && message ? ' — ' : ''}{message}
      </span>
      {action}
      {dismissible && (
        <button onClick={() => { setVisible(false); onDismiss?.(); }} aria-label="Dismiss"
          style={{ background: 'none', border: 'none', color: F.sub, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 2 }}>✕</button>
      )}
    </FioriMessageStrip>
  );
}

// ── Toolbar — search + filters + actions. Drop-in for AoneActionBar.
export function FioriActionBar({ search, filters, primary, secondary, bulkActions, selectedCount = 0, onClearSelection, exportFn, style }) {
  // Callers pass either React nodes or AoneActionBar's action descriptors
  // ({label, onClick, disabled}) — accept both.
  const render = (a, variant) => {
    if (!a) return null;
    const one = (x, i) => (x && typeof x === 'object' && !React.isValidElement(x) && x.label)
      ? <FioriButton key={x.label || i} variant={x.variant || variant} onClick={x.onClick} disabled={x.disabled} icon={x.icon}>{x.label}</FioriButton>
      : x;
    return Array.isArray(a) ? a.map(one) : one(a, 0);
  };
  primary = render(primary, 'primary');
  secondary = render(secondary, 'secondary');
  bulkActions = render(bulkActions, 'secondary');
  // search: accept a React node OR AoneActionBar's {value, onChange, placeholder} descriptor.
  if (search && typeof search === 'object' && !React.isValidElement(search)) {
    search = <FioriSearch value={search.value} onChange={search.onChange} placeholder={search.placeholder} />;
  }
  return (
    <div style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10,
      flexWrap: 'wrap', fontFamily: F.font,
    }, style)}>
      {selectedCount > 0 ? (
        <>
          <span style={{ fontSize: 13, fontWeight: 700, color: F.ink }}>{selectedCount} selected</span>
          {bulkActions}
          <button onClick={onClearSelection} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: F.blue, fontSize: 12, cursor: 'pointer', fontFamily: F.font }}>Clear</button>
        </>
      ) : (
        <>
          {search && <div style={{ flex: '1 1 240px', minWidth: 200 }}>{search}</div>}
          {filters}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {exportFn && <FioriButton variant="transparent" onClick={exportFn}>⤓ Export</FioriButton>}
            {secondary}
            {primary}
          </div>
        </>
      )}
    </div>
  );
}

// ── Right panel — Fiori "flexible column" detail. Drop-in for AoneRightPanel.
export function FioriRightPanel({ open, onClose, title, subtitle, children, actions, width = 480, mobileActions }) {
  const { isMobile } = useResponsive();
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);
  if (!open) return null;

  // On a phone a record is a SCREEN, not a drawer. A 480px panel capped to
  // 96vw slid in from the right over a 375px display, leaving a sliver of the
  // list behind it and a ✕ in the corner where a back arrow belongs. It opens
  // as a full sheet instead, with one primary action pinned where a thumb
  // reaches rather than a row of buttons along the bottom edge.
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1300, background: F.bg,
        display: 'flex', flexDirection: 'column', fontFamily: F.font,
      }}>
        <div style={{ padding: '10px 14px', background: F.surface, borderBottom: `1px solid ${F.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} aria-label="Back" style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: F.ink, cursor: 'pointer', padding: '2px 6px 2px 0' }}>‹</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: F.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 0 92px', WebkitOverflowScrolling: 'touch' }}>{children}</div>
        {/* mobileActions or nothing. Falling back to `actions` put the desk's
            five buttons — Export PDF, Tally XML, Print… — along the bottom of
            a phone, which is the row this sheet exists to replace. */}
        {mobileActions && (
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 14px calc(12px + env(safe-area-inset-bottom, 0px))',
            background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(12px)', borderTop: `1px solid ${F.border}`,
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            {mobileActions}
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(38,30,24,0.42)', zIndex: 1300, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{
        width, maxWidth: '96vw', height: '100%', background: F.bg, borderLeft: `1px solid ${F.border}`,
        boxShadow: F.shadowLg, display: 'flex', flexDirection: 'column', fontFamily: F.font,
      }}>
        <div style={{ padding: '12px 18px', background: F.surface, borderBottom: `1px solid ${F.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: F.ink }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: F.sub, marginTop: 1 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 18, color: F.sub, cursor: 'pointer', padding: 4, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>{children}</div>
        {actions && (
          <div style={{ padding: '10px 18px', background: F.surface, borderTop: `1px solid ${F.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bottom sheet ─────────────────────────────────────────────────────────────
// A small decision, taken without leaving the record.
//
// On a desk a change like this is a right panel or a modal. On a phone it is a
// sheet: the record stays visible behind it, the reach is short, and dismissing
// is a tap outside rather than hunting a ✕. Above the breakpoint it renders as
// a centred dialog, so one call site serves both.
export function FioriSheet({ open, onClose, title, hint, children, action, footnote }) {
  const { isMobile } = useResponsive();
  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);
  if (!open) return null;

  const panel = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: F.surface, fontFamily: F.font,
        ...(isMobile
          ? { borderRadius: '22px 22px 0 0', padding: '16px 16px calc(22px + env(safe-area-inset-bottom, 0px))', boxShadow: '0 -14px 40px rgba(0,0,0,0.3)' }
          : { borderRadius: 16, padding: 22, width: 440, maxWidth: '94vw', boxShadow: F.shadowLg }),
      }}
    >
      {isMobile && <div style={{ width: 36, height: 4, borderRadius: 99, background: F.border, margin: '0 auto 13px' }} />}
      {title && <div style={{ fontFamily: F.display || F.font, fontWeight: 500, fontSize: 19, margin: '0 0 4px', color: F.ink }}>{title}</div>}
      {hint && <p style={{ fontSize: 12, color: F.muted, lineHeight: 1.5, margin: '0 0 13px' }}>{hint}</p>}
      {children}
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
      {footnote && <p style={{ fontSize: 11.5, color: F.muted, textAlign: 'center', margin: '10px 0 0', lineHeight: 1.5 }}>{footnote}</p>}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(24,20,16,0.42)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      }}
    >{panel}</div>
  );
}

// A labelled field for a sheet. Not AoneInput — that carries the desk's
// density and its own label renderer, and three label renderers for one
// element is already a recorded problem in this codebase.
export function FioriSheetField({ label, value, onChange, placeholder, type = 'text', inputMode, autoFocus }) {
  return (
    <label style={{ display: 'block', border: `1px solid ${F.border}`, borderRadius: 11, padding: '9px 12px', marginBottom: 9, background: F.bg, cursor: 'text' }}>
      <span style={{ display: 'block', fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: F.sub, fontWeight: 700, marginBottom: 3 }}>{label}</span>
      <input
        value={value} onChange={(e) => onChange?.(e.target.value)} placeholder={placeholder}
        type={type} inputMode={inputMode} autoFocus={autoFocus}
        style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily: F.font, fontSize: 14.5, fontWeight: 600, color: F.ink, padding: 0 }}
      />
    </label>
  );
}

// ── Toast stack — Fiori message toast (bottom-centre, neutral ink).
export function FioriToastStack({ toasts = [] }) {
  const tone = { success: F.ok, warning: F.warn, danger: F.bad, error: F.bad, info: F.ink };
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: tone[t.variant] || F.ink, color: '#fff', borderRadius: F.r,
          padding: '8px 16px', fontSize: 13, fontFamily: F.font, boxShadow: F.shadowLg, maxWidth: 520,
        }}>{t.message}</div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Object-page primitives — added for the sales module redesign.
//
// Fiori's object page is what makes a CRM feel like one system: a fixed header
// identifying the record, a path showing where it sits in its lifecycle, and
// "related lists" underneath pulling in the child records. These four
// components are generic — they know nothing about deals — so any AONE object
// (a PO, a formulation) can adopt the same shape later.
// ─────────────────────────────────────────────────────────────────────────────

// ── Object header — title block + key/value facts strip + actions.
// `facts` is [{label, value, tone}]; keep it to 4-6 items, it is the "at a
// glance" row a rep reads before doing anything else.
export function FioriObjectHeader({
  eyebrow, title, subtitle, status, statusState, facts = [], actions, children, style,
}) {
  return (
    <div style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      padding: '14px 18px', fontFamily: F.font, marginBottom: 12,
    }, style)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          {eyebrow && (
            <div style={{ fontSize: 11, fontWeight: 700, color: F.sub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{eyebrow}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 2 }}>
            <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: F.ink, letterSpacing: '-0.01em' }}>{title}</h2>
            {status && <FioriStatus status={status} state={statusState} />}
          </div>
          {subtitle && <div style={{ fontSize: 13, color: F.sub, marginTop: 3 }}>{subtitle}</div>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>{actions}</div>}
      </div>
      {facts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, marginTop: 14, borderTop: `1px solid ${F.line}`, paddingTop: 12 }}>
          {facts.map((f, i) => (
            <div key={f.label} style={{
              padding: i === 0 ? '0 22px 0 0' : '0 22px',
              borderLeft: i > 0 ? `1px solid ${F.line}` : 'none', minWidth: 110,
            }}>
              <div style={{ fontSize: 11, color: F.sub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: f.tone || F.ink, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{f.value}</div>
            </div>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Path indicator — the Salesforce "path" chevrons.
// `steps` is [{key, label, done, count}]; `current` marks the active step.
// Completed steps fill burgundy, the current one outlines it, future steps stay
// paper. Clicking a step calls onSelect(key) when provided.
export function FioriPath({ steps = [], current, onSelect, style }) {
  return (
    <div style={merge({ display: 'flex', gap: 3, flexWrap: 'wrap', fontFamily: F.font, marginBottom: 12 }, style)}>
      {steps.map((s) => {
        const isCurrent = s.key === current;
        const bg = isCurrent ? F.blue : s.done ? F.blueSoft : F.surface;
        const fg = isCurrent ? '#fff' : s.done ? F.blue : F.muted;
        return (
          <button
            key={s.key}
            onClick={onSelect ? () => onSelect(s.key) : undefined}
            title={s.count != null ? `${s.count} record(s)` : undefined}
            style={{
              flex: '1 1 120px', minWidth: 110, border: `1px solid ${isCurrent ? F.blue : F.border}`,
              background: bg, color: fg, cursor: onSelect ? 'pointer' : 'default',
              padding: '8px 12px', fontFamily: F.font, fontSize: 12, fontWeight: 700,
              textAlign: 'left', borderRadius: F.r, display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', gap: 6,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {s.done && !isCurrent && <span aria-hidden>✓</span>}
              {s.label}
            </span>
            {s.count != null && s.count > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.85 }}>{s.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Related list — a child-record table docked under an object header.
// Collapsed by default when empty so an object page with no quotes yet doesn't
// read as broken; `columns` is [{key, label, render?, width?, align?}].
export function FioriRelatedList({
  title, count, columns = [], rows = [], onRowClick, action, emptyMessage = 'No records', style,
}) {
  const [open, setOpen] = useState(true);
  return (
    <div style={merge({
      background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.rCard,
      marginBottom: 12, fontFamily: F.font, overflow: 'hidden',
    }, style)}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '10px 14px', borderBottom: open ? `1px solid ${F.border}` : 'none', background: F.gridHead,
      }}>
        <button onClick={() => setOpen((o) => !o)} style={{
          border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
          display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: F.font,
          fontSize: 13, fontWeight: 700, color: F.ink,
        }}>
          <span aria-hidden style={{ fontSize: 10, color: F.sub }}>{open ? '▼' : '▶'}</span>
          {title}
          <span style={{ color: F.sub, fontWeight: 600 }}>({count != null ? count : rows.length})</span>
        </button>
        {action}
      </div>
      {open && (
        rows.length === 0 ? (
          <div style={{ padding: '18px 14px', fontSize: 13, color: F.muted }}>{emptyMessage}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{columns.map((c, ci) => (
                  <th key={ci} style={{ ...fioriTh, width: c.width, textAlign: c.align || 'left' }}>{c.label}</th>
                ))}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = F.rowHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {columns.map((c, ci) => (
                      <td key={ci} style={{ ...fioriTd, textAlign: c.align || 'left' }}>
                        {/* Same (value, row) signature as FioriDataTable — the two
                            must not diverge or columns stop being portable. */}
                        {c.render ? c.render(r[c.key], r) : (r[c.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ── Kanban board — deal pipeline, one column per stage.
// Drag-and-drop uses the native HTML5 API rather than a library: the payload is
// a single record id, and the drop handler is the caller's business logic
// (onDrop(cardId, stage)) so this component stays domain-agnostic.
export function FioriKanban({ columns = [], renderCard, onDrop, formatValue, style }) {
  const [over, setOver] = useState(null);
  return (
    <div style={merge({
      display: 'flex', gap: 10, overflowX: 'auto', alignItems: 'flex-start',
      paddingBottom: 12, fontFamily: F.font,
    }, style)}>
      {columns.map((col) => (
        <div
          key={col.stage}
          onDragOver={(e) => { if (onDrop) { e.preventDefault(); setOver(col.stage); } }}
          onDragLeave={() => setOver((s) => (s === col.stage ? null : s))}
          onDrop={(e) => {
            setOver(null);
            if (!onDrop) return;
            const id = e.dataTransfer.getData('text/plain');
            if (id) onDrop(id, col.stage);
          }}
          style={{
            flex: '0 0 268px', width: 268, background: over === col.stage ? F.rowSel : F.bg,
            border: `1px solid ${over === col.stage ? F.blue : F.border}`,
            borderRadius: F.rCard, display: 'flex', flexDirection: 'column', maxHeight: '70vh',
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${F.border}`, background: F.surface, borderRadius: `${F.rCard}px ${F.rCard}px 0 0` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: F.ink, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{col.stage}</span>
              <span style={{ fontSize: 12, color: F.sub, fontWeight: 600 }}>{col.count}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: F.blue, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>
              {formatValue ? formatValue(col.value) : col.value}
            </div>
          </div>
          <div style={{ padding: 8, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {col.deals.length === 0
              ? <div style={{ fontSize: 12, color: F.muted, padding: '10px 4px' }}>Nothing here</div>
              : col.deals.map((d) => (
                <div key={d.id}
                  draggable={!!onDrop}
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', d.id)}
                  style={{
                    background: F.surface, border: `1px solid ${F.border}`, borderRadius: F.r,
                    padding: '9px 11px', cursor: onDrop ? 'grab' : 'pointer', boxShadow: F.shadow,
                  }}
                >{renderCard(d)}</div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Gaps the Aone* set has and Fiori did not: divider, spinner, and a
// white-ground page. The Sales module runs on a pure-white ground (the ivory
// paper reads yellow next to Fiori's grey grid chrome); the creator hub keeps F.bg.
export function FioriDivider({ label, style }) {
  return (
    <div style={merge({ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', fontFamily: F.font }, style)}>
      <div style={{ flex: 1, height: 1, background: F.line }} />
      {label && <span style={{ fontSize: 12, color: F.sub, whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</span>}
      {label && <div style={{ flex: 1, height: 1, background: F.line }} />}
    </div>
  );
}

export function FioriSpinner({ size = 24, color = F.blue }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid ${color}28`, borderTop: `2px solid ${color}`,
      animation: 'spin 0.7s linear infinite', display: 'inline-block',
    }} />
  );
}

export function FioriPageLight(props) {
  return <FioriPage {...props} style={merge({ background: '#FFFFFF' }, props.style)} />;
}
